import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { Button, Result, Spin } from "antd";
import { useUser } from "../context/UserContext";
import { isSessionRejected } from "../helpers/api";
import { getAccessToken } from "../helpers/auth";

const centered = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "calc(100vh - var(--glut-env-banner-height))",
} as const;

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading, error, refetch } = useUser();
  if (!getAccessToken()) return <Navigate to="/login" replace />;
  if (loading) {
    return (
      <div style={centered}>
        <Spin size="large" />
      </div>
    );
  }
  if (!user) {
    // We hold a token but /me never came back. Only the server rejecting it means the
    // session is over — anything else is the API being unreachable (in local development,
    // mid-restart), and sending the user to /login for that loses the page they were on
    // and reads as a spurious logout.
    if (!isSessionRejected(error)) {
      return (
        <div style={centered}>
          <Result
            status="warning"
            title="Can't reach the server"
            subTitle="Your session is still valid. Retry once the API is back up."
            extra={
              <Button type="primary" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        </div>
      );
    }
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}
